import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { eur, STATUS_LABEL } from "../../lib/format";
import { Loader2, X } from "lucide-react";

const STATUSES = ["itinerary_sent", "requested", "availability_confirmed", "waiting_for_payment", "deposit_paid", "confirmed", "enriched", "completed", "cancelled"];

export default function AdminBookings() {
  const [bookings, setBookings] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => setBookings([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/bookings/${id}/status?status=${status}`);
      toast.success("Status updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  if (bookings === null) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#B08D57" }} /></div>;

  return (
    <div data-testid="admin-bookings">
      <h2 className="font-heading text-2xl mb-5">All Bookings</h2>
      <div className="rounded-2xl border overflow-hidden bg-white" style={{ borderColor: "#E5E7EB" }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left" style={{ background: "#F5F2EC", color: "#6B7280" }}>
              <th className="px-5 py-3 font-medium">Guest</th>
              <th className="px-5 py-3 font-medium">Retreat</th>
              <th className="px-5 py-3 font-medium">Nights</th>
              <th className="px-5 py-3 font-medium">Total</th>
              <th className="px-5 py-3 font-medium">Paid</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((b) => (
              <tr key={b.booking_id} className="border-t" style={{ borderColor: "#E5E7EB" }} data-testid={`admin-booking-${b.booking_id}`}>
                <td className="px-5 py-4">
                  <p className="font-medium">{b.guest_name}</p>
                  <p className="text-xs" style={{ color: "#6B7280" }}>{b.user_email}</p>
                </td>
                <td className="px-5 py-4">{b.package_name}<br /><span className="text-xs" style={{ color: "#6B7280" }}>{b.area}</span></td>
                <td className="px-5 py-4">{b.nights}</td>
                <td className="px-5 py-4">{eur(b.pricing.total)}</td>
                <td className="px-5 py-4">{eur(b.amount_paid || 0)}</td>
                <td className="px-5 py-4">
                  <select value={b.status} onChange={(e) => setStatus(b.booking_id, e.target.value)} data-testid={`status-select-${b.booking_id}`}
                    className="border rounded-lg px-2 py-1.5 text-xs bg-white" style={{ borderColor: "#E5E7EB" }}>
                    {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                </td>
                <td className="px-5 py-4">
                  <button onClick={() => setEditing(b)} data-testid={`enrich-btn-${b.booking_id}`}
                    className="text-xs px-3 py-1.5 rounded-lg text-white" style={{ background: "#111111" }}>Enrich</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {bookings.length === 0 && <p className="p-8 text-center" style={{ color: "#6B7280" }}>No bookings yet.</p>}
      </div>

      {editing && <EnrichModal booking={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function EnrichModal({ booking, onClose, onSaved }) {
  const p = booking.itinerary?.pickup || {};
  const [form, setForm] = useState({
    driver_name: p.driver_name || "",
    driver_phone: p.driver_phone || "",
    flight_number: p.flight_number || "",
    pickup_time: p.pickup_time || "",
    villa_name: booking.villa_name || "",
    concierge_note: booking.concierge_note || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/bookings/${booking.booking_id}/enrich`, form);
      toast.success("Itinerary enriched");
      onSaved();
    } catch { toast.error("Save failed"); setSaving(false); }
  };

  const field = (key, label, placeholder) => (
    <div>
      <label className="text-xs uppercase tracking-wider" style={{ color: "#6B7280" }}>{label}</label>
      <input value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} placeholder={placeholder}
        data-testid={`enrich-${key}`} className="w-full border rounded-lg px-3 py-2.5 mt-1 text-sm" style={{ borderColor: "#E5E7EB" }} />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="enrich-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-2xl">Enrich Arrival Details</h3>
          <button onClick={onClose}><X className="w-5 h-5" style={{ color: "#6B7280" }} /></button>
        </div>
        <p className="text-sm mb-5" style={{ color: "#6B7280" }}>{booking.guest_name} · {booking.package_name}</p>
        <div className="space-y-4">
          {field("pickup_time", "Pickup Time", "e.g. 14:30, 12 Aug")}
          {field("flight_number", "Flight Number", "e.g. EK 654")}
          {field("driver_name", "Chauffeur Name", "e.g. Nuwan Perera")}
          {field("driver_phone", "Driver Phone", "e.g. +94 77 123 4567")}
          {field("villa_name", "Villa Name", "e.g. Villa Serenity")}
          <div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "#6B7280" }}>Concierge Note</label>
            <textarea value={form.concierge_note} onChange={(e) => setForm({ ...form, concierge_note: e.target.value })} rows={3}
              data-testid="enrich-concierge_note" className="w-full border rounded-lg px-3 py-2.5 mt-1 text-sm" style={{ borderColor: "#E5E7EB" }} />
          </div>
        </div>
        <button onClick={save} disabled={saving} data-testid="enrich-save-btn"
          className="w-full mt-6 py-3 rounded-xl text-white flex items-center justify-center gap-2" style={{ background: "#111111" }}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & Notify Guest"}
        </button>
      </div>
    </div>
  );
}
